/**
 * Key bindings and the gamepad (Phase 8): every action shows its key and an alternate; clicking
 * one captures the next key or mouse button (Escape cancels). Conflicts are shown, never
 * refused. The bindings follow the account through the settings sync.
 */
import {
  ACTION_LABELS,
  ACTIONS,
  bindingConflicts,
  bindingLabel,
  DEFAULT_BINDINGS,
  rebind,
  type Action,
  type Binding,
} from '@steelwake/shared/progression';
import { Button, colors, spacing } from '@steelwake/ui';
import { useEffect, useState } from 'react';

import { Row, Section } from './layout.js';
import { useSettingsStore } from './store.js';

function BindingButton({ action, slot }: { action: Action; slot: 0 | 1 }) {
  const current = useSettingsStore((s) => s.settings.bindings[action][slot]);
  const update = useSettingsStore((s) => s.update);
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!capturing) return;
    const finish = (binding: Binding | null): void => {
      setCapturing(false);
      if (binding === null) return;
      const bindings = useSettingsStore.getState().settings.bindings;
      update({ bindings: rebind(bindings, action, slot, binding) });
    };
    // Capture phase and immediate stop: the match's own key handler never sees the press.
    const onKey = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopImmediatePropagation();
      finish(e.code === 'Escape' ? null : e.code);
    };
    const onMouse = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopImmediatePropagation();
      finish(`Mouse${String(e.button)}`);
    };
    const swallow = (e: Event): void => e.preventDefault();
    window.addEventListener('keydown', onKey, { capture: true });
    // The click that started the capture is still in flight; mouse buttons count from now on.
    const frame = requestAnimationFrame(() => {
      window.addEventListener('mousedown', onMouse, { capture: true });
      window.addEventListener('contextmenu', swallow, { capture: true });
    });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('mousedown', onMouse, { capture: true });
      window.removeEventListener('contextmenu', swallow, { capture: true });
    };
  }, [capturing, action, slot, update]);

  const label = capturing ? 'Press a key…' : current === undefined ? '—' : bindingLabel(current);
  return (
    <Button
      variant="ghost"
      data-testid={`bind-${action}-${String(slot)}`}
      style={{ minWidth: 96, padding: '4px 8px', fontSize: 12 }}
      onClick={() => setCapturing(true)}
    >
      {label}
    </Button>
  );
}

export function ControlsSection() {
  const bindings = useSettingsStore((s) => s.settings.bindings);
  const gamepad = useSettingsStore((s) => s.settings.gamepad);
  const update = useSettingsStore((s) => s.update);
  const conflicts = bindingConflicts(bindings);

  return (
    <>
      <Section title="Key bindings">
        {ACTIONS.map((action) => (
          <Row key={action} label={ACTION_LABELS[action]}>
            <BindingButton action={action} slot={0} />
            <BindingButton action={action} slot={1} />
            {bindings[action].length > 1 ? (
              <Button
                variant="ghost"
                data-testid={`unbind-${action}-1`}
                aria-label={`Remove the alternate for ${ACTION_LABELS[action]}`}
                style={{ padding: '4px 8px', fontSize: 12 }}
                onClick={() => update({ bindings: rebind(bindings, action, 1, null) })}
              >
                ×
              </Button>
            ) : null}
          </Row>
        ))}
        {conflicts.length > 0 ? (
          <div
            data-testid="binding-conflicts"
            style={{ color: colors.warning, fontSize: 13, marginTop: spacing.sm }}
          >
            {conflicts.map((c) => (
              <div key={c.binding}>
                {bindingLabel(c.binding)} is bound to{' '}
                {c.actions.map((a) => ACTION_LABELS[a].toLowerCase()).join(' and ')}.
              </div>
            ))}
          </div>
        ) : null}
        <div style={{ marginTop: spacing.sm }}>
          <Button
            variant="ghost"
            data-testid="reset-bindings"
            onClick={() => update({ bindings: DEFAULT_BINDINGS })}
          >
            Reset bindings
          </Button>
        </div>
      </Section>

      <Section title="Gamepad">
        <Row label="Gamepad (standard layout: sticks move and aim, triggers fire)">
          <input
            type="checkbox"
            checked={gamepad.enabled}
            data-testid="gamepad-enabled"
            onChange={(e) => update({ gamepad: { ...gamepad, enabled: e.target.checked } })}
          />
        </Row>
        <Row label={`Aim sensitivity · ${gamepad.sensitivity.toFixed(2)}`}>
          <input
            type="range"
            min={0.2}
            max={3}
            step={0.05}
            value={gamepad.sensitivity}
            data-testid="gamepad-sensitivity"
            onChange={(e) =>
              update({ gamepad: { ...gamepad, sensitivity: Number(e.target.value) } })
            }
          />
        </Row>
        <Row label={`Stick dead zone · ${String(Math.round(gamepad.deadZone * 100))}%`}>
          <input
            type="range"
            min={0}
            max={0.5}
            step={0.01}
            value={gamepad.deadZone}
            data-testid="gamepad-deadzone"
            onChange={(e) => update({ gamepad: { ...gamepad, deadZone: Number(e.target.value) } })}
          />
        </Row>
      </Section>
    </>
  );
}
